import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Truck, 
  Package, 
  RefreshCw, 
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock,
  Key,
  Send,
  X,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RepairsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("inbound");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchRepairs = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setRefreshing(true);
      const response = await axios.get(`${API}/repairs/data`, { timeout: 60000 });
      setData(response.data);
      setLoading(false);
      setRefreshing(false);
    } catch (e) {
      console.error("Error fetching repairs data:", e);
      toast.error("Failed to fetch repairs data");
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRepairs(true);
  };

  useEffect(() => {
    fetchRepairs();
    // Auto-refresh every 2 minutes
    const interval = setInterval(() => fetchRepairs(), 120000);
    return () => clearInterval(interval);
  }, [fetchRepairs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-600" />
        <span className="ml-3 text-lg text-slate-600">Loading repairs data...</span>
      </div>
    );
  }

  const OrderRow = ({ order, showExpedite = false }) => (
    <tr 
      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${order.needs_expedite ? 'bg-red-50 hover:bg-red-100' : ''}`}
      onClick={() => setSelectedOrder(order)}
      data-testid={`repair-order-${order.order_id}`}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <span className="font-mono font-medium text-slate-700">{order.order_id}</span>
          {order.needs_expedite && (
            <Badge variant="destructive" className="text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Expedite
            </Badge>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="text-slate-700">{order.product}</span>
      </td>
      <td className="py-3 px-4">
        <Badge 
          variant="outline" 
          className={
            order.state === "shipped" ? "bg-emerald-100 text-emerald-800" :
            order.state === "in_repair" ? "bg-amber-100 text-amber-800" :
            "bg-blue-100 text-blue-800"
          }
        >
          {order.state === "shipped" ? "Shipped" : 
           order.state === "in_repair" ? "In Repair" : "In Transit"}
        </Badge>
      </td>
      <td className="py-3 px-4 text-slate-600 text-sm">
        {order.date_inbound_shipping || "-"}
      </td>
      <td className="py-3 px-4">
        <Badge 
          variant="outline" 
          className={order.service_type === "Warranty" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}
        >
          {order.service_type || "N/A"}
        </Badge>
      </td>
      <td className="py-3 px-4 text-right">
        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
      </td>
    </tr>
  );

  // Order Details Modal
  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;

    const getStatusBadge = () => {
      switch (order.state) {
        case "shipped":
          return <Badge className="bg-emerald-500">Shipped</Badge>;
        case "in_repair":
          return <Badge className="bg-amber-500">In Repair</Badge>;
        default:
          return <Badge className="bg-blue-500">In Transit</Badge>;
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold">#{order.order_id}</span>
              {getStatusBadge()}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Details */}
          <div className="p-4 space-y-4">
            <DetailRow label="Order ID" value={order.order_id} />
            <DetailRow label="Product" value={order.product} />
            <DetailRow label="Service Type" value={order.service_type || "-"} highlight={order.service_type === "Paid Service"} />
            <DetailRow label="Charge" value={order.charge ? `₹${order.charge}` : "-"} />
            <DetailRow label="Inbound Date" value={order.date_inbound_shipping || "-"} />
            <DetailRow label="Received Date" value={order.date_inbound_receipt || "-"} />
            <DetailRow label="Outbound Date" value={order.date_outbound_shipping || "-"} />
            <DetailRow label="Return Order ID" value={order.return_order_id || "-"} />
            <DetailRow label="Delivery Type" value={order.inbound_delivery_type || "-"} />
            <DetailRow label="Shipping Fee" value={order.fee || "-"} />
            <DetailRow label="Customer" value={order.customer_name || "-"} />
            <DetailRow label="Issues" value={order.issues || "None reported"} />
            {order.hours_in_repair !== null && (
              <DetailRow 
                label="Time in Repair" 
                value={`${order.hours_in_repair} hours`} 
                highlight={order.needs_expedite}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const DetailRow = ({ label, value, highlight = false }) => (
    <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium ${highlight ? 'text-red-600' : 'text-slate-900'}`}>{value}</span>
    </div>
  );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="repairs-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-heading">Repairs Tracking</h2>
          <p className="text-slate-600 mt-1">Track warranty and service orders</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="repairs-refresh-button"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* OTP Section */}
      {data?.otps_today?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              OTPs for Today's Inbound Shipments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {data.otps_today.map((otp, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-amber-200"
                >
                  <span className="text-2xl font-mono font-bold text-amber-700">{otp.otp}</span>
                  {otp.order_id && (
                    <span className="text-xs text-slate-500">({otp.order_id})</span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Inbound Transit</CardTitle>
            <Truck className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 font-mono">
              {data?.summary?.inbound_transit || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Awaiting receipt</p>
          </CardContent>
        </Card>

        <Card className={`card-hover ${data?.summary?.needs_expedite > 0 ? 'border-red-300 bg-red-50' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">In Repair</CardTitle>
            <Wrench className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${data?.summary?.needs_expedite > 0 ? 'text-red-600' : 'text-amber-600'}`}>
              {data?.summary?.in_repair || 0}
            </div>
            {data?.summary?.needs_expedite > 0 && (
              <Badge variant="destructive" className="mt-1">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {data.summary.needs_expedite} need expediting
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Shipped</CardTitle>
            <Send className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600 font-mono">
              {data?.summary?.shipped || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Returned to customer</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Orders</CardTitle>
            <Package className="w-4 h-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono">
              {data?.summary?.total || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">All repair orders</p>
          </CardContent>
        </Card>

        <Card className={`card-hover ${data?.summary?.needs_expedite > 0 ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Status</CardTitle>
            {data?.summary?.needs_expedite > 0 ? (
              <AlertTriangle className="w-4 h-4 text-red-500" />
            ) : (
              <CheckCircle className="w-4 h-4 text-emerald-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-lg font-bold ${data?.summary?.needs_expedite > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {data?.summary?.needs_expedite > 0 ? 'Action Required' : 'All Good'}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {data?.summary?.needs_expedite > 0 
                ? `${data.summary.needs_expedite} orders > 24h` 
                : 'No overdue repairs'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different states */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="inbound" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Inbound Transit ({data?.summary?.inbound_transit || 0})
          </TabsTrigger>
          <TabsTrigger value="repair" className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            In Repair ({data?.summary?.in_repair || 0})
            {data?.summary?.needs_expedite > 0 && (
              <Badge variant="destructive" className="ml-1">{data.summary.needs_expedite}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="shipped" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Shipped ({data?.summary?.shipped || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbound">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">
                Inbound Transit Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">ORDER ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">PRODUCT</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">STATUS</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">DATE</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">TYPE</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.inbound_transit?.length > 0 ? (
                      data.inbound_transit.map((order, idx) => (
                        <OrderRow key={idx} order={order} />
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-500">
                          <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          No orders in transit
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="repair">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                In Repair Orders
                {data?.summary?.needs_expedite > 0 && (
                  <Badge variant="destructive">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {data.summary.needs_expedite} need attention
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">ORDER ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">PRODUCT</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">STATUS</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">DATE</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">TYPE</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.in_repair?.length > 0 ? (
                      data.in_repair.map((order, idx) => (
                        <OrderRow key={idx} order={order} showExpedite={true} />
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-500">
                          <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          No orders currently in repair
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shipped">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">
                Shipped Orders (Completed)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">ORDER ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">PRODUCT</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">STATUS</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">DATE</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">TYPE</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.shipped?.length > 0 ? (
                      data.shipped.map((order, idx) => (
                        <OrderRow key={idx} order={order} />
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-500">
                          <Send className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          No shipped orders
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}

      {/* Last updated */}
      {data?.last_updated && (
        <div className="text-center text-sm text-slate-400">
          <Clock className="w-4 h-4 inline mr-1" />
          Data last updated: {new Date(data.last_updated).toLocaleString()}
        </div>
      )}
    </div>
  );
}
